// src/components/FD/TransactionHistory.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { formatDateToDDMMYYYY } from '../../utils/dateUtils';
import '../AdminDashboard/ExpenseManager.css';

const TransactionHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTransactions();
  }, []);

  useEffect(() => {
    if (searchValue.trim() === '') {
      setFilteredTransactions(transactions);
    } else {
      // Filter is handled by backend, but we can also filter here for immediate feedback
      setFilteredTransactions(transactions);
    }
  }, [transactions, searchValue]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const params = searchValue.trim() ? { search_value: searchValue.trim() } : {};
      const response = await axios.get(`${API_BASE_URL}/api/fd/transaction-history`, { params });
      setTransactions(response.data);
      setFilteredTransactions(response.data);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      alert('Failed to load transaction history');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchTransactions();
  };

  const clearSearch = () => {
    setSearchValue('');
    fetchTransactions();
  };

  const printTransactionHistory = () => {
    if (filteredTransactions.length === 0) {
      alert('No transactions to print.');
      return;
    }

    const total = filteredTransactions.reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
    const searchInfo = searchValue ? `Search: ${searchValue}` : 'All Transactions';

    const historyContent = `
<style>
  body { font-family: Arial, sans-serif; margin: 20px; padding: 20px; }
  .header { text-align: center; font-weight: bold; font-size: 1.5em; margin-bottom: 5px; }
  .sub-header { text-align: center; font-size: 1em; margin-bottom: 15px; }
  .search-info { text-align: center; margin-bottom: 20px; font-weight: bold; }
  table { width: 100%; border-collapse: collapse; margin-top: 20px; }
  th, td { border: 1px solid #000; padding: 8px; text-align: left; }
  th { background-color: #f0f0f0; font-weight: bold; }
  .right { text-align: right; }
  .total-row { background-color: #f0f0f0; font-weight: bold; }
  .footer { text-align: center; margin-top: 30px; font-size: 0.9em; }
</style>

<div class="header">M/S SHREE MAHAKAL FINANCE AND LEASING (R)</div>
<div class="sub-header">Khajuri, Aland , Kalaburgi</div>
<div class="search-info">FD Component - Transaction History</div>
<div class="search-info">${searchInfo}</div>

<table>
  <thead>
    <tr>
      <th>Date</th>
      <th>Registration No</th>
      <th>Name</th>
      <th>Phone No</th>
      <th>Transaction Type</th>
      <th>Payment Month</th>
      <th class="right">Amount (₹)</th>
      <th>Payment Mode</th>
      <th>Transaction ID</th>
    </tr>
  </thead>
  <tbody>
    ${filteredTransactions.map(t => `
      <tr>
        <td>${formatDateToDDMMYYYY(t.transaction_date)}</td>
        <td>${t.registration_no}</td>
        <td>${t.name}</td>
        <td>${t.phone_no}</td>
        <td>${t.transaction_type}</td>
        <td>${t.payment_month || '-'}</td>
        <td class="right">${Number(t.amount).toFixed(2)}</td>
        <td>${t.payment_mode || '-'}</td>
        <td>${t.transaction_id || '-'}</td>
      </tr>
    `).join('')}
    <tr class="total-row">
      <td colspan="6" style="text-align: right;"><strong>TOTAL:</strong></td>
      <td class="right"><strong>₹ ${total.toFixed(2)}</strong></td>
      <td colspan="2"></td>
    </tr>
  </tbody>
</table>

<div class="footer">
  Printed on: ${formatDateToDDMMYYYY(new Date().toISOString().split('T')[0])}
</div>
`;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(historyContent);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="transaction-history-section">
      <h3>Transaction History</h3>
      
      <div className="filters-container">
        <div className="filter-group">
          <label>Search by Registration No, Name, or Phone No:</label>
          <input
            type="text"
            className="filter-input"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Enter registration no, name, or phone number"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
          />
        </div>
        <div className="filter-buttons">
          <button className="clear-button" onClick={clearSearch}>
            Clear
          </button>
          <button className="search-button" onClick={handleSearch}>
            Search
          </button>
          <button className="print-button" onClick={printTransactionHistory}>
            Print
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>Loading transactions...</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="transactions-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Registration No</th>
                <th>Name</th>
                <th>Phone No</th>
                <th>Transaction Type</th>
                <th>Payment Month</th>
                <th>Amount (₹)</th>
                <th>Payment Mode</th>
                <th>Transaction ID</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.length > 0 ? (
                filteredTransactions.map((t) => (
                  <tr key={t.id}>
                    <td>{formatDateToDDMMYYYY(t.transaction_date)}</td>
                    <td>{t.registration_no}</td>
                    <td>{t.name}</td>
                    <td>{t.phone_no}</td>
                    <td>{t.transaction_type}</td>
                    <td>{t.payment_month || '-'}</td>
                    <td>₹{Number(t.amount).toFixed(2)}</td>
                    <td>{t.payment_mode || '-'}</td>
                    <td>{t.transaction_id || '-'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '20px' }}>
                    No transactions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TransactionHistory;

